/**
 * Group Members Modal
 * Csoport tagok megjelenítése
 */

import React, { useEffect, useState } from 'react';
import { CustomerGroup } from '../../types/customerGroup';
import { useCustomers } from '../../hooks/useCustomers';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import { XMarkIcon, UsersIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import SearchableSelect from '../common/SearchableSelect';
import toast from 'react-hot-toast';

interface GroupMembersModalProps {
  group: CustomerGroup | null;
  onClose: () => void;
}

const GroupMembersModal: React.FC<GroupMembersModalProps> = ({ group, onClose }) => {
  const { customers, loading } = useCustomers();
  const { addCustomers, removeCustomers } = useCustomerGroups();
  const [showAddCustomer, setShowAddCustomer] = useState(false);

  // Számítjuk ki a csoport tagjait közvetlenül, ne tároljuk state-ben
  const groupCustomers = group && customers 
    ? customers.filter(c => group.customerIds.includes(c.id))
    : [];

  const handleAddCustomer = async (customerId: string) => {
    if (!group || !customerId) return;
    
    try {
      await addCustomers(group.id, [customerId]);
      toast.success('Ügyfél hozzáadva a csoporthoz');
      setShowAddCustomer(false);
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    }
  };

  const handleRemoveCustomer = async (customerId: string) => {
    if (!group) return;
    
    try {
      await removeCustomers(group.id, [customerId]);
      toast.success('Ügyfél eltávolítva a csoportból');
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    }
  };

  // Elérhető ügyfelek (akik még nincsenek a csoportban)
  const availableCustomers = customers.filter(c => !group?.customerIds.includes(c.id));

  if (!group) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl shadow-lg ring-2 ring-white dark:ring-gray-700"
                style={{ 
                  background: `linear-gradient(135deg, ${group.color || '#3B82F6'}50, ${group.color || '#3B82F6'}30)`,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <span className="drop-shadow-md">{group.icon || '👥'}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {group.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {group.customerCount} tag
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddCustomer(!showAddCustomer)}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Ügyfél hozzáadása
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Ügyfél hozzáadása */}
            {showAddCustomer && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Válassz ügyfelet
                </label>
                <SearchableSelect
                  options={availableCustomers.map(customer => ({
                    value: customer.id,
                    label: customer.type === 'organization' 
                      ? customer.companyName 
                      : `${customer.lastName} ${customer.firstName}`
                  }))}
                  value=""
                  onChange={handleAddCustomer}
                  placeholder="Keresés ügyfelek között..."
                  searchPlaceholder="Keresés..."
                  clearable={false}
                />
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : groupCustomers.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Még nincs tag ebben a csoportban
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {groupCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                          {customer.type === 'organization' 
                            ? customer.companyName?.charAt(0) || '?'
                            : `${customer.firstName?.charAt(0) || ''}${customer.lastName?.charAt(0) || ''}`
                          }
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {customer.type === 'organization'
                            ? customer.companyName
                            : `${customer.lastName} ${customer.firstName}`
                          }
                        </div>
                        {customer.email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {customer.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {customer.type === 'organization' ? '🏢' : '👤'}
                      </div>
                      <button
                        onClick={() => handleRemoveCustomer(customer.id)}
                        className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Eltávolítás a csoportból"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Bezárás
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupMembersModal;
